<template>
  <div class="absolute top-0 left-0 right-0 flex items-center h-16 z-40 font-inter">
    <div class="flex flex-grow px-6">
      <div class="flex text-black opacity-80 uppercase text-lg font-bold items-center gap-1">
        <RouterLink to="/">Lettergrid</RouterLink>
        <span class="text-sm">{{ gameMode }}</span>
      </div>

      <div class="flex items-center text-black opacity-80 ml-auto -mr-2">
        <!-- Mobile dropdown -->
        <div ref="menuRoot" class="relative md:hidden">
          <button
            class="ml-2 w-10 h-10 flex items-center justify-center rounded hover:bg-black/5 focus:outline-none focus:ring-2 focus:ring-black/20"
            :aria-expanded="isMenuOpen ? 'true' : 'false'"
            aria-haspopup="menu"
            aria-label="Open menu"
            @click="toggleMenu"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="lucide lucide-menu-icon lucide-menu"
            >
              <path d="M4 5h16" />
              <path d="M4 12h16" />
              <path d="M4 19h16" />
            </svg>
          </button>

          <div
            v-if="isMenuOpen"
            class="absolute right-0 mt-2 w-44 rounded-lg bg-white shadow-lg ring-1 ring-black/10 overflow-hidden"
            role="menu"
          >
            <button
              class="w-full px-4 py-3 text-left hover:bg-black/5 focus:outline-none focus:bg-black/5"
              role="menuitem"
              @click="openModal('stat_modal')"
            >
              Stats
            </button>
            <button
              class="w-full px-4 py-3 text-left hover:bg-black/5 focus:outline-none focus:bg-black/5"
              role="menuitem"
              @click="openModal('endless_modal')"
            >
              Endless
            </button>
            <button
              class="w-full px-4 py-3 text-left hover:bg-black/5 focus:outline-none focus:bg-black/5"
              role="menuitem"
              @click="openModal('help_modal')"
            >
              Help
            </button>
            <button
              class="w-full px-4 py-3 text-left hover:bg-black/5 focus:outline-none focus:bg-black/5"
              role="menuitem"
              @click="openModal('data_modal')"
            >
              Data
            </button>
          </div>
        </div>

        <!-- Desktop buttons -->
        <button
          class="ml-2 hidden md:inline-flex"
          @click="$store.commit('setActiveModal', 'stat_modal')"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            class="w-8 h-8 px-1"
          >
            <path
              d="M15.5 2A1.5 1.5 0 0014 3.5v13a1.5 1.5 0 001.5 1.5h1a1.5 1.5 0 001.5-1.5v-13A1.5 1.5 0 0016.5 2h-1zM9.5 6A1.5 1.5 0 008 7.5v9A1.5 1.5 0 009.5 18h1a1.5 1.5 0 001.5-1.5v-9A1.5 1.5 0 0010.5 6h-1zM3.5 10A1.5 1.5 0 002 11.5v5A1.5 1.5 0 003.5 18h1A1.5 1.5 0 006 16.5v-5A1.5 1.5 0 004.5 10h-1z"
            />
          </svg>
        </button>
        <button
          class="ml-2 hidden md:inline-flex"
          @click="$store.commit('setActiveModal', 'endless_modal')"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="lucide lucide-infinity-icon lucide-infinity"
          >
            <path d="M6 16c5 0 7-8 12-8a4 4 0 0 1 0 8c-5 0-7-8-12-8a4 4 0 1 0 0 8" />
          </svg>
        </button>
        <button
          class="ml-2 hidden md:inline-flex"
          @click="$store.commit('setActiveModal', 'help_modal')"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            class="w-8 h-8 px-1"
          >
            <path
              fill-rule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM8.94 6.94a.75.75 0 11-1.061-1.061 3 3 0 112.871 5.026v.345a.75.75 0 01-1.5 0v-.5c0-.72.57-1.172 1.081-1.287A1.5 1.5 0 108.94 6.94zM10 15a1 1 0 100-2 1 1 0 000 2z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
        <button
          class="ml-2 hidden md:inline-flex"
          @click="$store.commit('setActiveModal', 'data_modal')"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            class="w-8 h-8 px-1"
          >
            <path
              d="M21 6.375c0 2.692-4.03 4.875-9 4.875S3 9.067 3 6.375 7.03 1.5 12 1.5s9 2.183 9 4.875Z"
            />
            <path
              d="M12 12.75c2.685 0 5.19-.586 7.078-1.609a8.283 8.283 0 0 0 1.897-1.384c.016.121.025.244.025.368C21 12.817 16.97 15 12 15s-9-2.183-9-4.875c0-.124.009-.247.025-.368a8.285 8.285 0 0 0 1.897 1.384C6.809 12.164 9.315 12.75 12 12.75Z"
            />
            <path
              d="M12 16.5c2.685 0 5.19-.586 7.078-1.609a8.282 8.282 0 0 0 1.897-1.384c.016.121.025.244.025.368 0 2.692-4.03 4.875-9 4.875s-9-2.183-9-4.875c0-.124.009-.247.025-.368a8.284 8.284 0 0 0 1.897 1.384C6.809 15.914 9.315 16.5 12 16.5Z"
            />
            <path
              d="M12 20.25c2.685 0 5.19-.586 7.078-1.609a8.282 8.282 0 0 0 1.897-1.384c.016.121.025.244.025.368 0 2.692-4.03 4.875-9 4.875s-9-2.183-9-4.875c0-.124.009-.247.025-.368a8.284 8.284 0 0 0 1.897 1.384C6.809 19.664 9.315 20.25 12 20.25Z"
            />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { gameNumber } from "../common/helpers";

export default {
  data() {
    return {
      gameNumber: gameNumber(),
      isMenuOpen: false,
    };
  },

  computed: {
    gameMode() {
      if (this.$route.name === "Main") {
        return "";
      }

      if (["Daily", "DailyScore"].includes(this.$route.name)) {
        return ` - Daily #${this.gameNumber}`;
      }

      if (["Endless", "EndlessScore"].includes(this.$route.name)) {
        return " - Endless";
      }
    },
  },

  mounted() {
    document.addEventListener("click", this.onDocumentClick);
    document.addEventListener("keydown", this.onKeydown);
  },

  beforeDestroy() {
    document.removeEventListener("click", this.onDocumentClick);
    document.removeEventListener("keydown", this.onKeydown);
  },

  methods: {
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
    },

    openModal(modal) {
      this.$store.commit("setActiveModal", modal);
      this.isMenuOpen = false;
    },

    onDocumentClick(event) {
      if (!this.isMenuOpen) return;
      const root = this.$refs.menuRoot;
      if (!root) return;
      if (root.contains(event.target)) return;
      this.isMenuOpen = false;
    },

    onKeydown(event) {
      if (event.key !== "Escape") return;
      this.isMenuOpen = false;
    },
  },
};
</script>

<style></style>



// WEBPACK FOOTER //
// src/components/UserBar.vue